import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { ButtonComponent } from '@components/button/button.component';
import { AlertOptions, AlertService } from '@services/alert.service';
import { LoadingService } from '@services/loading.service';

@Component({
  imports: [ButtonComponent],
  selector: 'app-alert',
  styleUrl: './alert.component.scss',
  templateUrl: './alert.component.html',
})
export class AlertComponent implements AfterViewInit {
  readonly alert = input.required<AlertOptions>();
  private alertService = inject(AlertService);
  private dialog = viewChild.required<ElementRef<HTMLElement>>('dialog');
  private loading = inject(LoadingService).get();

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      if (!this.loading() && this.alertService.get()() === this.alert()) {
        this.dialog().nativeElement.focus();
      }
    });
  }

  @HostListener('document:focusin', ['$event'])
  protected onFocus(event: FocusEvent): void {
    if (!this.loading() && !this.dialog().nativeElement.contains(event.target as Node)) {
      this.dialog().nativeElement.focus();
    }
  }

  @HostListener('document:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (this.loading()) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.dismiss();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    const buttons = Array.from(this.dialog().nativeElement.querySelectorAll<HTMLButtonElement>('button'));
    const first = buttons[0];
    const last = buttons.at(-1);
    if (!first) {
      event.preventDefault();
      this.dialog().nativeElement.focus();
    } else if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog().nativeElement)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  protected dismiss(role = 'cancel'): void {
    this.alertService.close(role);
  }
}
